import * as React from 'react'
import cx from 'classnames'
import styles from './ModalSheet.sass'
import { useOnClickOutside } from 'hooks/click-outside'
import { IconButton } from '../IconButton'
import Icon from 'components/Icon'
import { useModalStack } from '../../hooks/modal-stack'
import { animated, to } from 'react-spring'
import ScrollShadow from '../ScrollShadow'
import { useScrollOffsetSpring } from '../../hooks/spring-hooks'
import { clamp } from '../../utils/math-util'
import { useCssTransition } from '../../hooks/transitioning'
import { mergeRefs } from '../../utils/react-util'
import FullscreenOverlay from 'components/FullscreenOverlay'
import { observer } from 'mobx-react'

export * from './ModalSheetSearchHeader'
export * from './ModalSheetHeroHeader'

/**
 * Modal sheet context props.
 */
interface ModalSheetContextProps {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  close?: () => unknown
}

/**
 * Used to pass a ref from the `ModalSheetContent` around.
 */
export const ModalSheetContext = React.createContext<ModalSheetContextProps>({
  scrollRef: React.createRef(),
})

interface ModalSheetProps {
  children?: React.ReactNode
  showing: boolean
  disableCloseOnEsc?: boolean
  disableCloseOnOutsideClick?: boolean
  onRequestClose?: () => unknown
  large?: boolean
  leftSidePanelContent?: React.ReactNode
}

/**
 * Modal sheet. Like Dialog, but better.
 * Prefer using ModalSheet for modals.
 */
export const ModalSheet = observer(
  React.forwardRef(function ModalSheet(
    {
      showing,
      onRequestClose,
      disableCloseOnEsc,
      disableCloseOnOutsideClick,
      children,
      large,
      leftSidePanelContent,
    }: ModalSheetProps,
    ref: React.Ref<HTMLDivElement>,
  ) {
    const contentScrollRef = React.useRef<HTMLDivElement | null>(null)
    const close = onRequestClose
    const contextProps: ModalSheetContextProps = React.useMemo(
      () => ({
        scrollRef: contentScrollRef,
        close,
      }),
      [close],
    )
    const { isFirstResponder, stackDepthStyle } = useModalStack(
      {
        showing,
        close,
      },
      {
        disableCloseOnEsc,
      },
    )
    const sheetRef = React.useRef<HTMLDivElement | null>(null)
    useOnClickOutside(
      sheetRef,
      isFirstResponder && !disableCloseOnOutsideClick ? close : undefined,
    )

    const [transitionRef, rendering, hidden] = useCssTransition(showing)

    return (
      rendering && (
        <FullscreenOverlay preventScroll showing={!hidden} ref={transitionRef}>
          <ModalSheetContext.Provider value={contextProps}>
            <div className={cx(styles.container, hidden && styles.hidden)}>
              <animated.div
                className={cx(
                  styles.sheetContainer,
                  large && styles.large,
                  leftSidePanelContent &&
                    styles.leftSidePanelContentSheetContainer,
                )}
                // For some reason react-spring craps out
                // at the end of the CSS transition when we set
                // `rendering` to false. By removing the style before that happens,
                // we avoid that. Since the style is only used by
                // non-hidden modals, it won't make a difference
                // in terms of output.
                // Alternatively, do all transitions with react-spring,
                // but that's a rabbit hole for another day.
                style={!hidden ? stackDepthStyle : undefined}
              >
                {leftSidePanelContent ? (
                  <div
                    ref={mergeRefs([ref, sheetRef])}
                    className={styles.modalGroup}
                  >
                    {leftSidePanelContent}
                    <div
                      className={cx(
                        styles.sheet,
                        isFirstResponder && styles.firstResponder,
                        leftSidePanelContent &&
                          styles.leftSidePanelContentSheet,
                      )}
                    >
                      {children}
                    </div>
                  </div>
                ) : (
                  <div
                    ref={mergeRefs([ref, sheetRef])}
                    className={cx(
                      styles.sheet,
                      isFirstResponder && styles.firstResponder,
                    )}
                  >
                    {children}
                  </div>
                )}
              </animated.div>
            </div>
          </ModalSheetContext.Provider>
        </FullscreenOverlay>
      )
    )
  }),
)

interface LeftSidePanelProps {
  children?: React.ReactNode
}

/**
 * Left side panel for the modal sheet.
 */
export function LeftSidePanel({ children }: LeftSidePanelProps) {
  return <div className={styles.leftSidePanel}>{children}</div>
}

interface LeftSidePanelHeadingProps {
  children?: React.ReactNode
}

/**
 * Heading for the left side panel.
 */
export function LeftSidePanelHeading({ children }: LeftSidePanelHeadingProps) {
  return <div className={styles.leftSidePanelHeading}>{children}</div>
}

interface LeftSidePanelTitleProps {
  children?: React.ReactNode
}

/**
 * Title for the left side panel.
 */
export function LeftSidePanelTitle({ children }: LeftSidePanelTitleProps) {
  return <div className={styles.leftSidePanelTitle}>{children}</div>
}

interface LeftSidePanelDescriptionProps {
  children?: React.ReactNode
}

/**
 * Description for the left side panel.
 */
export function LeftSidePanelDescription({
  children,
}: LeftSidePanelDescriptionProps) {
  return <div className={styles.leftSidePanelDescription}>{children}</div>
}

interface ModalSheetHeaderShellProps {
  children?: React.ReactNode
  subheader?: React.ReactNode
  size: 'sm' | 'base' | 'auto'
  bordered?: boolean
  /**
   * If `true`, will add border radius to the bottom as well since there is
   * no sibling to add it.
   */
  lonelyChild?: boolean
}

/**
 * The outer-most part of a Modal Sheet header.
 * For convenience, use `ModalSheetHeader`.
 */
export function ModalSheetHeaderShell({
  children,
  subheader,
  lonelyChild,
  bordered,
  size,
}: ModalSheetHeaderShellProps) {
  const { scrollRef } = React.useContext(ModalSheetContext)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  return (
    <div
      className={cx(
        styles.headerShell,
        lonelyChild && styles.lonely,
        bordered && styles.bordered,
        !subheader && styles[`size-${size}`],
      )}
    >
      <div className={styles.headerContainer}>{children}</div>
      {subheader}
      <ScrollShadow opacity={to(scrollOffset, (v) => clamp(v / 50))} />
    </div>
  )
}

export interface ModalSheetHeaderProps {
  children?: React.ReactNode
  subheader?: React.ReactNode
  withCloseButton?: boolean
  closeButtonDataTest?: string
  bordered?: boolean
  /**
   * If `true`, will add border radius to the bottom as well since there is
   * no sibling to add it.
   */
  lonelyChild?: boolean
  shellSize?: 'sm' | 'base' | 'auto'
}

/**
 * Header for the modal sheet.
 */
export function ModalSheetHeader({
  children,
  subheader,
  withCloseButton,
  lonelyChild,
  bordered,
  closeButtonDataTest,
  shellSize = 'base',
}: ModalSheetHeaderProps) {
  const { close } = React.useContext(ModalSheetContext)
  return (
    <ModalSheetHeaderShell
      subheader={subheader}
      size={shellSize}
      bordered={bordered}
      lonelyChild={lonelyChild}
    >
      <div className={cx(styles.headerContent)}>{children}</div>
      {withCloseButton && close && (
        <div className={cx(styles.closeBtnContainer)}>
          <ModalSheetHeaderCloseButton dataTest={closeButtonDataTest} />
        </div>
      )}
    </ModalSheetHeaderShell>
  )
}

interface ModalSheetHeaderCloseButtonProps {
  onClick?: () => void
  dataTest?: string
}

/**
 * Close button for the Modal Sheet Header.
 */
export function ModalSheetHeaderCloseButton({
  dataTest,
}: ModalSheetHeaderCloseButtonProps) {
  const { close } = React.useContext(ModalSheetContext)

  return (
    <IconButton className={styles.closeBtn} onClick={close} dataTest={dataTest}>
      <Icon name="close" />
    </IconButton>
  )
}

interface ModalSheetTitleProps {
  children: React.ReactNode
  emphasized?: boolean
  large?: boolean
}

/**
 * Modal sheet title. Put this in a `ModalSheetHeader`.
 */
export function ModalSheetTitle({
  children,
  emphasized,
  large,
}: ModalSheetTitleProps) {
  return (
    <div
      className={cx(
        styles.title,
        emphasized && styles.emphasized,
        large && styles.large,
      )}
    >
      {children}
    </div>
  )
}

interface ModalSheetHeaderDescriptionProps {
  children: React.ReactNode
}

/**
 * Modal sheet header description. Put this in a `ModalSheetHeader`.
 */
export function ModalSheetHeaderDescription({
  children,
}: ModalSheetHeaderDescriptionProps) {
  return <div className={styles.headerDescription}>{children}</div>
}

interface ModalSheetTitleEmphasisProps {
  children?: React.ReactNode
}

/**
 * Modal Sheet Title Emphasis. Used to emphasize a span of text inside of a `ModalSheetHeaderTitle`.
 */
export function ModalSheetTitleEmphasis({
  children,
}: ModalSheetTitleEmphasisProps) {
  return <span className={styles.emphasized}>{children}</span>
}

interface ModalSheetContentProps {
  children: React.ReactNode
  leftAligned?: boolean
  expandable?: boolean
}

/**
 * Content for the Modal sheet. Will scroll. Sticks to the bottom on smaller screens.
 */
export const ModalSheetContent = React.forwardRef(function ModalSheetContent(
  { children, leftAligned, expandable }: ModalSheetContentProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const { scrollRef } = React.useContext(ModalSheetContext)

  return (
    <div
      ref={mergeRefs([ref, scrollRef])}
      className={cx(
        styles.content,
        leftAligned && styles.leftAligned,
        expandable && styles.expandable,
      )}
    >
      {children}
    </div>
  )
})

interface ModalSheetSectionProps {
  children?: React.ReactNode
  spaced?: boolean
  horizontallySpaced?: boolean
  bordered?: boolean
}

/**
 * Bordered section, to be used in `ModalSheetContent`.
 */
export function ModalSheetSection({
  children,
  spaced,
  horizontallySpaced,
  bordered,
}: ModalSheetSectionProps) {
  return (
    <div
      className={cx(
        styles.section,
        spaced && styles.spaced,
        horizontallySpaced && styles.horizontallySpaced,
        bordered && styles.bordered,
      )}
    >
      {children}
    </div>
  )
}

interface ModalSheetFooterProps {
  children?: React.ReactNode
}

/**
 * Modal sheet footer.
 */
export function ModalSheetFooter({ children }: ModalSheetFooterProps) {
  return <div className={styles.footer}>{children}</div>
}

interface ModalSheetFooterButtonGroupProps {
  children?: React.ReactNode
  stacked?: boolean
}

/**
 * Button group for Modal sheet footer. Ensures
 * spacing between buttons. Use `Button` components as children.
 */
export function ModalSheetFooterButtonGroup({
  children,
  stacked,
}: ModalSheetFooterButtonGroupProps) {
  return (
    <div className={cx(styles.footerBtnGroup, stacked && styles.stacked)}>
      {React.Children.map(children, (c, i) => (
        <div
          key={i}
          className={cx(styles.footerBtn, stacked && styles.stacked)}
        >
          {c}
        </div>
      ))}
    </div>
  )
}

interface ModalSheetFooterSpacerProps {
  children?: React.ReactNode
}

/**
 * By default footer items are right-aligned.
 * To have a section to the left and spacing in between, plop
 * this bad boi in the middle.
 * @param children
 * @constructor
 */
export function ModalSheetFooterSpacer({
  children,
}: ModalSheetFooterSpacerProps) {
  return <div className={styles.footerSpacer}>{children}</div>
}

interface ModalSheetFooterSectionProps {
  children?: React.ReactNode
}

/**
 * Footer section. Use this for non-button groups.
 * @param children
 * @constructor
 */
export function ModalSheetFooterSection({
  children,
}: ModalSheetFooterSectionProps) {
  return <div className={styles.footerSection}>{children}</div>
}

interface ModalSheetContentBlockProps {
  children?: React.ReactNode
  dataTest?: string
  bordered?: boolean
  expanded?: boolean
}

/**
 " Modal Sheet Content Block
 */
export function ModalSheetContentBlock({
  children,
  dataTest,
  bordered,
  expanded,
}: ModalSheetContentBlockProps) {
  return (
    <div
      className={cx(
        styles.contentBlock,
        bordered && styles.bordered,
        expanded && styles.expanded,
      )}
      data-test={dataTest}
    >
      {children}
    </div>
  )
}

interface ModalSheetContentIllustrationProps {
  src: string
  alt?: string
  title?: string
}

/**
 * Image used for modal content, for example in a Confirm.
 * @param src
 * @param alt
 * @param title
 * @constructor
 */
export function ModalSheetContentIllustration({
  src,
  alt,
  title,
}: ModalSheetContentIllustrationProps) {
  return (
    <div className={styles.contentIllustration}>
      <img
        className={styles.contentIllustrationImage}
        src={src}
        alt={alt}
        title={title}
      />
    </div>
  )
}

type Align = 'left' | 'center' | 'right'

interface ModalSheetContentTitleProps {
  children?: React.ReactNode
  dataTest?: string
  compact?: boolean
  align?: Align
}

/**
 " Modal Sheet Content Title
 */
export function ModalSheetContentTitle({
  children,
  dataTest,
  compact,
  align,
}: ModalSheetContentTitleProps) {
  return (
    <div
      className={cx(
        styles.contentTitle,
        align && styles[align],
        compact && styles.compact,
      )}
      data-test={dataTest}
    >
      {children}
    </div>
  )
}

interface ModalSheetContentDescriptionProps {
  children?: React.ReactNode
  dataTest?: string
  compact?: boolean
  align?: Align
}

/**
 " Modal Sheet Content Description
 */
export function ModalSheetContentDescription({
  children,
  dataTest,
  compact,
  align,
}: ModalSheetContentDescriptionProps) {
  return (
    <div
      className={cx(
        styles.contentDescription,
        align && styles[align],
        compact && styles.compact,
      )}
      data-test={dataTest}
    >
      {children}
    </div>
  )
}

/**
 * Modal Sheet List Foundation
 */

interface ModalSheetListHeaderProps {
  children: React.ReactNode
}

/**
 * A header with a darker background color and list padding.
 * Useful for lists or sections
 */
export function ModalSheetListHeader({ children }: ModalSheetListHeaderProps) {
  return <div className={styles.listHeader}>{children}</div>
}

interface ModalSheetListItemProps {
  children: React.ReactNode
}

/**
 * Wrapper for a list item. Includes padding
 * and border
 */
export function ModalSheetListItem({ children }: ModalSheetListItemProps) {
  return <div className={styles.listItem}>{children}</div>
}

interface ModalSheetSpacerProps {
  children?: React.ReactNode
}

export function ModalSheetSpacer({ children }: ModalSheetSpacerProps) {
  return <div className={styles.spacer}>{children}</div>
}

interface ModalSheetContentBoxProps {
  children: React.ReactNode
  color?: 'blue' | 'green' | 'red' | 'grey'
  ['data-test']?: string
}

/**
 * Padded box for content with optional colors.
 */
export function ModalSheetContentBox({
  children,
  color,
  'data-test': dataTest,
}: ModalSheetContentBoxProps) {
  return (
    <div
      data-test={dataTest}
      className={cx(styles.contentBox, color && styles[color])}
    >
      {children}
    </div>
  )
}
